import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreId,
  extractNumber,
  HxTimePipe,
  HxTimesheetService,
  isoDate,
  StoreBasicModel,
  TimesheetInfoModel,
  TimesheetModel,
  TimesheetType,
  toRouterQueryParams,
  UserBasicModel
} from 'hx-services';
import { Subscription } from 'rxjs';
import { getISODay } from 'date-fns';
import { TimesheetEditComponent } from '@manager-app/pages/timesheet/edit/timesheet-edit.component';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'app-timesheet-list',
    templateUrl: './timesheet-list.component.html',
    styleUrls: ['./timesheet-list.component.css'],
  imports: [HxStoreSelectComponent, FormsModule, NgFor, NgIf, NgClass, HxLoaderComponent, DatePipe, TranslocoPipe, HxTimePipe, HxDateRangeSelectComponent]
})
export class TimesheetListComponent implements OnInit, OnDestroy {
  list: TimesheetInfoModel[] = [];
  isLoading = {
    list: false,
  };
  params: {
    storeId?: number;
    date?: DateRange,
  } = {};
  dateinfos: {date: string, dayOfWeek: number}[] = [];
  entranceTime: string;
  exitTime: string;
  type: TimesheetType;
  TimesheetType = TimesheetType;
  Attendance = TimesheetType.ATTENDANCE;
  private sub: Subscription;

  constructor(
    private router: Router,
    private aRoute: ActivatedRoute,
    private modal: NgbModal,
    private tr: TranslocoService,
    private timesheetService: HxTimesheetService
  ) {
  }

  ngOnInit(): void {
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.storeId = extractNumber('storeId', paramMap) ?? extractDefaultStoreId();
      const now = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(weekAgo), to: isoDate(now)};
      if (this.params.storeId) {
        this.loadList();
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    this.applyFilters();
  }

  applyFilters() {
    this.navigateUrl();
  }

  openModal(timesheet: TimesheetModel, user: UserBasicModel) {
    const modalInstance = this.modal.open(TimesheetEditComponent, {size: 'sm'});
    modalInstance.componentInstance.timesheet = timesheet;
    modalInstance.componentInstance.user = user;

    modalInstance.result
      .then(() => {
        this.loadList();
      })
      .catch(err => {
        console.error(err);
      });
  }

  openBonusModal(timesheet: TimesheetModel, user: UserBasicModel) {
    const modalInstance = this.modal.open(TimesheetEditComponent, {size: 'md'});
    modalInstance.componentInstance.timesheet = timesheet;
    modalInstance.componentInstance.user = user;
    modalInstance.componentInstance.timesheetType = TimesheetType.BONUS;

    modalInstance.result
      .then(() => {
        this.loadList();
      })
      .catch(err => {
        console.error(err);
      });
  }

  private navigateUrl() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadList() {
    this.isLoading.list = true;
    this.list = [];
    this.dateinfos = this.getDatesBetweenDates(this.params.date.from, this.params.date.to);
    this.timesheetService.getTimesheetList({ storeId: this.params.storeId, fromDate: this.params.date.from, toDate: this.params.date.to}).then(result => {
      this.list = result;
      this.isLoading.list = false;
    }).catch(() => this.isLoading.list = false);
  }

  private getDatesBetweenDates(
    start: string,
    end: string
  ): { date: string; dayOfWeek: number }[] {

    const result: { date: string; dayOfWeek: number }[] = [];

    // Преобразуем в UTC-даты без смещения
    let current = this.toUtcDate(start);
    const endDate = this.toUtcDate(end);

    while (current.getTime() <= endDate.getTime()) {
      const iso = this.toIsoDateOnly(current); // yyyy-MM-dd
      result.push({
        date: iso,
        dayOfWeek: getISODay(current) // из date-fns
      });

      // +1 день в UTC
      current = new Date(Date.UTC(
        current.getUTCFullYear(),
        current.getUTCMonth(),
        current.getUTCDate() + 1
      ));
    }

    return result;
  }

  private toUtcDate(str: string): Date {
    // "2024-12-03" → Date в UTC без сдвига
    const [y, m, d] = str.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  private toIsoDateOnly(date: Date): string {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
